import React, { useMemo } from 'react';
import {
    IRhColumnsItem,
    RhColumns,
    RhDateInput,
    RhNumberField,
    RHTextField,
} from '../../../../../../dum';
import {
    ACCOUNT,
    EXPENSE_RESOURCES,
    IExpense,
    UNIT_PRICE,
} from '../expense-form';
import { ResourcesInput } from './resources/resources-input';
import { useCalculateExpenseResourcesTotal } from '../use-calculate-expense-resources-total';
import { useWatch } from 'react-hook-form';
import { FIELD_LABELS, shortLabel } from '../../../../../../../helpers';
import { useFieldRevert } from '../use-field-revert';
import { RevertHint } from '../revert-hint';

const defaultValue: IExpense['expense_resources'][number] = {
    units: 0,
    resource_id: null,
    unit_price: 0,
    notes: '',
    date: null,
};

const UNITS: keyof IExpense['expense_resources'][number] = 'units';
const DATE: keyof IExpense['expense_resources'][number] = 'date';
const NOTES: keyof IExpense['expense_resources'][number] = 'notes';

// The per-line subtotal (unit_price) is reversible the same way the document
// `tax` is: when it changes from what the expense loaded with — a manual edit
// or the IVA splitter overwriting it — a "Valor original · Revertir" hint
// appears under the field. Its own component so useFieldRevert runs once per
// row, keyed by that row's field path.
function ResourceUnitPriceInput({
    index,
    onChange,
}: {
    index: number;
    onChange: () => void;
}) {
    const name = `${EXPENSE_RESOURCES}.${index}.${UNIT_PRICE}`;
    const { original, hasChanged, revert } = useFieldRevert(name);
    // A row loaded from the saved expense carries an `id`; a row the user just
    // added does not. Only existing rows have a real "valor original" to revert
    // to — a new row's original is just the 0 default, so offering "revertir a
    // $0" there is noise. Gate the hint on the row being persisted.
    const rowId = useWatch({ name: `${EXPENSE_RESOURCES}.${index}.id` });
    const isExistingRow = rowId !== null && rowId !== undefined;
    return (
        <RhNumberField
            name={name}
            label={shortLabel(FIELD_LABELS.unit_price)}
            disableGutters
            onChange={onChange}
            helperText={
                isExistingRow ? (
                    <RevertHint
                        hasChanged={hasChanged}
                        original={original}
                        onRevert={() => {
                            revert();
                            onChange();
                        }}
                    />
                ) : undefined
            }
        />
    );
}

export function ExpenseResourcesColumns() {
    const { calculateExpenseResourcesTotal } =
        useCalculateExpenseResourcesTotal();

    const account = useWatch({
        name: ACCOUNT,
    }) as IExpense['account'];

    const items: IRhColumnsItem[] = useMemo(() => {
        const baseItems: IRhColumnsItem[] = [
            {
                cellGrow: 2,
                getField: (index: number) => {
                    return <ResourcesInput index={index} />;
                },
            },
            {
                getField: (index: number) => {
                    return (
                        <RhNumberField
                            name={`${EXPENSE_RESOURCES}.${index}.${UNITS}`}
                            label={shortLabel(FIELD_LABELS.units)}
                            disableGutters
                            onChange={() => {
                                calculateExpenseResourcesTotal();
                            }}
                        />
                    );
                },
            },
            {
                getField: (index: number) => {
                    return (
                        <ResourceUnitPriceInput
                            index={index}
                            onChange={calculateExpenseResourcesTotal}
                        />
                    );
                },
            },
        ];

        if (account && account.monitor_supplier_expenses) {
            baseItems.push({
                getField: (index: number) => {
                    return (
                        <RhDateInput
                            name={`${EXPENSE_RESOURCES}.${index}.${DATE}`}
                            label={shortLabel(FIELD_LABELS.date)}
                            disableGutters
                        />
                    );
                },
            });
            baseItems.push({
                cellGrow: 2,
                getField: (index: number) => {
                    return (
                        <RHTextField
                            name={`${EXPENSE_RESOURCES}.${index}.${NOTES}`}
                            label={shortLabel(FIELD_LABELS.notes)}
                            disableGutters
                        />
                    );
                },
            });
        }

        return baseItems;
    }, [account, calculateExpenseResourcesTotal]);

    return (
        <RhColumns
            disableDelete={(index) => index === 0}
            title={''}
            name={EXPENSE_RESOURCES}
            defaultValue={defaultValue}
            items={items}
            onDelete={() => {
                calculateExpenseResourcesTotal();
            }}
        />
    );
}
